import { Newspaper } from '@mui/icons-material'
import { Button } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table'
import { FunctionComponent, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import MuiRenderTopToolbarCustomActions from '../../../../base/components/MuiTable/MuiRenderTopToolbarCustomActions'
import StackMenu from '../../../../base/components/MyMenu/StackMenu'
import { StackMenuItem } from '../../../../base/components/MyMenu/StackMenuItem'
import { PAGE_DEFAULT, PageProps } from '../../../../interfaces'
import { genApiQuery } from '../../../../utils/helper'
import { MuiToolbarAlertBannerProps } from '../../../../utils/muiTable/materialReactTableUtils'
import { MuiTableAdvancedOptionsProps } from '../../../../utils/muiTable/muiTableAdvancedOptionsProps'
import { apiRecetasListado } from '../../api/apiRecetasListado'
import { RecetaProp } from '../../interfaces'
import { recetasRoutesMap } from '../../recetasRoutes'
import InsumosListado from './InsumosListado'
import { recetaColumns } from './recetaColumns'
import RecetaMenu from './recetaMenu'
import useAuth from '../../../../base/hooks/useAuth'
import { getEntidadInput } from '../../../../utils/getEntidadInput'

const RecetasListado: FunctionComponent = () => {
  const columns = useMemo<MRT_ColumnDef<RecetaProp>[]>(() => recetaColumns, [])
  // ESTADO DATATABLE
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  // const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: PAGE_DEFAULT.page,
    pageSize: PAGE_DEFAULT.limit,
  })
  const [rowCount, setRowCount] = useState<number>(0)
  // FIN ESTADO DATATABLE
  const { user } = useAuth()
  const entidad = useMemo(() => getEntidadInput(user), [user])
  const { data, isError, isRefetching, isLoading, refetch } = useQuery<RecetaProp[]>({
    queryKey: [
      'recetas',
      columnFilters,
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      entidad,
    ],
    queryFn: async () => {
      const query = genApiQuery(columnFilters, [
        `sucursal.codigo=${entidad.codigoSucursal}`,
      ])
      const fetchPagination: PageProps = {
        ...PAGE_DEFAULT,
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        reverse: sorting.length <= 0,
        query,
      }
      const { pageInfo, docs } = await apiRecetasListado(fetchPagination)
      setRowCount(pageInfo.totalDocs)
      return docs
    },
    refetchOnWindowFocus: true,
    refetchInterval: false,
  })
  const table = useMaterialReactTable({
    ...(MuiTableAdvancedOptionsProps as MRT_TableOptions<RecetaProp>),
    columns: columns,
    data: data || [],
    muiToolbarAlertBannerProps: MuiToolbarAlertBannerProps(isError),
    state: {
      isLoading,
      columnFilters,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      density: 'compact',
      sorting,
    },
    renderTopToolbarCustomActions: () => (
      <MuiRenderTopToolbarCustomActions refetch={refetch} />
    ),
    enableGlobalFilter: false,
    enableColumnFilters: false,
    enableHiding: false,
    enableSorting: false,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    rowCount,
    positionToolbarAlertBanner: 'bottom',
    enableExpandAll: true,
    renderDetailPanel(props) {
      return <InsumosListado receta={props.row.original} />
    },
    renderRowActions: ({ row }) => <RecetaMenu row={row.original} refetch={refetch} />,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        muiTableHeadCellProps: {
          align: 'center',
        },
        size: 120,
      },
    },
  })
  return (
    <>
      <StackMenu asideSidebarFixed>
        <StackMenuItem>
          <Button
            variant="contained"
            component={Link}
            to={recetasRoutesMap.registro.path}
            startIcon={<Newspaper />}
            color={'primary'}
          >
            {recetasRoutesMap.registro.name}
          </Button>
        </StackMenuItem>
      </StackMenu>
      <MaterialReactTable table={table} />
    </>
  )
}

export default RecetasListado
